// (c) 2023 ABBYY Development, Inc.

import {
    useRef,
    useState,
    ChangeEvent,
    useMemo,
    FC,
    useCallback,
    useEffect,
    ReactNode
} from 'react';
import { useTranslation } from 'react-i18next';
import { ListContainer } from './EditableListStyled';
import {
    ActionButton,
    Button,
    Dialog,
    InputWithValidation,
    IconButton,
    Table,
    Switch,
    Typography,
    AddFill,
    Delete,
    Edit,
    ValidationState
} from 'components';
import { AlertsError } from 'components/icons';
import { useTable, useKeyDown, useBoolean } from 'hooks';
import {
    ApplicationListItems,
    ApplicationListItemSetter
} from './ApplicationList';
import { FlexContainer } from './CommonStyled';
import { EditButtons } from '../CommonStyled';
import { useBlockNavigation } from 'providers';

interface EditableListProps {
    title: string;
    applicationList: ApplicationListItems;
    isTemplateReadOnly: boolean;
    itemSetter: ApplicationListItemSetter;
    hint: ReactNode;
}

const EditableList = ({
    applicationList: { items, isEnabled },
    itemSetter,
    isTemplateReadOnly,
    title,
    hint
}: EditableListProps) => {
    const { t } = useTranslation();
    const [isDialogOpen, openDialog, closeDialog] = useBoolean();
    const { setIsNavigationBlocked } = useBlockNavigation();
    const [editedItemIndex, setEditedItemIndex] = useState<number>(null);

    const deleteItems = useCallback(
        (itemsToDelete: object[]) => {
            itemSetter((prevState) => ({
                ...prevState,
                items: items.filter((item) => !itemsToDelete.includes(item))
            }));
            setIsNavigationBlocked(true);
        },
        [itemSetter, items, setIsNavigationBlocked]
    );

    const addItem = (item: string) => {
        closeDialog();
        itemSetter((prevState) => ({
            ...prevState,
            items: [...items, item]
        }));

        setIsNavigationBlocked(true);
    };

    const editItem = (item: string) => {
        closeDialog();

        const itemsCopy = [...items];
        itemsCopy[editedItemIndex] = item;

        itemSetter((prevState) => ({
            ...prevState,
            items: itemsCopy
        }));
        setIsNavigationBlocked(true);
    };

    const deleteSelectedItems = () => {
        deleteItems(table.selectedFlatRows.map((row) => row.original));
    };

    const table = useTable<object>({
        columns: useMemo(
            () => [
                {
                    Header: t('general.Item'),
                    accessor: '',
                    Cell: ({ row }) => <>{row.original}</>
                },
                {
                    Header: '',
                    id: 'action',
                    Cell: ({ row }) => (
                        <EditButtons>
                            <IconButton
                                isDisabled={!isEnabled || isTemplateReadOnly}
                                onClick={() => {
                                    setEditedItemIndex(parseInt(row.id));
                                    openDialog();
                                }}
                                icon={
                                    <Edit
                                        color={
                                            !isEnabled || isTemplateReadOnly
                                                ? 'grey'
                                                : 'primary'
                                        }
                                    />
                                }
                            />
                            <IconButton
                                isDisabled={!isEnabled || isTemplateReadOnly}
                                onClick={() => {
                                    deleteItems([row.original]);
                                }}
                                icon={
                                    <Delete
                                        color={
                                            !isEnabled || isTemplateReadOnly
                                                ? 'grey'
                                                : 'error'
                                        }
                                    />
                                }
                            />
                        </EditButtons>
                    )
                }
            ],
            [deleteItems, isEnabled, isTemplateReadOnly, openDialog, t]
        ),
        // @ts-ignore
        data: items,
        hasSelect: true,
        disableSelect: isTemplateReadOnly || !isEnabled
    });

    return (
        <>
            <ItemDialog
                isOpen={isDialogOpen}
                close={closeDialog}
                title={
                    editedItemIndex
                        ? t('expressions.edit_item')
                        : t('expressions.add_item')
                }
                initialValue={
                    editedItemIndex === null ? '' : items[editedItemIndex]
                }
                onSubmit={(item) =>
                    editedItemIndex === null ? addItem(item) : editItem(item)
                }
                items={items}
                hint={hint}
            />
            <ListContainer>
                <FlexContainer>
                    <Typography variant="subtitle1" transform="uppercase">
                        {title}
                    </Typography>
                    <Switch
                        isDisabled={isTemplateReadOnly}
                        isChecked={isEnabled}
                        onChange={() => {
                            setIsNavigationBlocked(true);
                            itemSetter((prevState) => ({
                                ...prevState,
                                isEnabled: !isEnabled
                            }));
                        }}
                        asteriskPosition="left"
                    />
                </FlexContainer>
                <FlexContainer>
                    <Button
                        isDisabled={!isEnabled || isTemplateReadOnly}
                        icon={<AddFill />}
                        onClick={() => {
                            setEditedItemIndex(null);
                            openDialog();
                        }}
                    >
                        {t('expressions.add_item')}
                    </Button>
                    <ActionButton
                        isDisabled={
                            !isEnabled ||
                            !table.selectedFlatRows.length ||
                            isTemplateReadOnly
                        }
                        icon={
                            <Delete
                                color={
                                    !isEnabled ||
                                    !table.selectedFlatRows.length ||
                                    isTemplateReadOnly
                                        ? 'disabled'
                                        : 'error'
                                }
                            />
                        }
                        onClick={deleteSelectedItems}
                    >
                        {t('expressions.delete_selected')}
                    </ActionButton>
                </FlexContainer>
                <Table controller={table} disablePagination />
            </ListContainer>
        </>
    );
};

interface ItemDialogProps {
    isOpen: boolean;
    close: () => void;
    title: string;
    onSubmit: (value: string) => void;
    initialValue: string;
    items: string[];
    hint: ReactNode;
}

const ItemDialog: FC<ItemDialogProps> = ({
    isOpen,
    close,
    title,
    onSubmit,
    initialValue,
    items,
    hint
}) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>();
    const [value, setValue] = useState(initialValue);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const submit = () => {
        onSubmit(value);
        setValue('');
    };

    useKeyDown(inputRef.current, 'Enter', () => {
        if (isOpen && !!value) {
            submit();
        }
    });

    const stateMap = useMemo(
        () => ({
            [ValidationState.Error]: {
                icon: (
                    <span>
                        <AlertsError size={16} />
                    </span>
                ),
                popper: {
                    content: value
                        ? t('expressions.cannot_be_duplicate')
                        : t('expressions.cannot_be_empty')
                },
                inputProps: { isError: true }
            }
        }),
        [t, value]
    );

    const validate = (value: string) => {
        if (!value || items.includes(value)) {
            setHasError(true);
            return ValidationState.Error;
        }

        setHasError(false);
        return ValidationState.Success;
    };

    return (
        <Dialog isOpen={isOpen} height={220} onClose={close}>
            <Dialog.Header title={t(title)} onClose={close} />
            <Dialog.Body>
                <InputWithValidation
                    isFullWidth
                    isFocused
                    value={value}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setValue(event.target.value)
                    }
                    stateMap={stateMap}
                    validate={validate}
                    inputRef={inputRef}
                />
                {hint}
            </Dialog.Body>
            <Dialog.Footer>
                <Button type="text" onClick={close}>
                    {t('general.Cancel')}
                </Button>
                <Button isDisabled={hasError} onClick={submit}>
                    {t('general.Add')}
                </Button>
            </Dialog.Footer>
        </Dialog>
    );
};

export default EditableList;
